<script setup lang="ts">
// Viewpart for the `search_web` tool: turns its result JSON into a clickable
// source list in the conversation. Registered in lib/toolRenderers.ts (tool
// identity -> renderer); rendered by ToolViewparts below the agent's prose.
// Collapsed by default (shared Fold) so a long source list doesn't pollute the
// thread - the user clicks "Sources" to expand.
//
// Reads the SAME data the ToolFold Copy uses (tool.outputFull), so there's no
// extra data path. The two search_web backends differ only in wrapping (fast
// lane = bare array, kernel MCP = { results: [...] }); both use
// {title, url, snippet}, so we normalize both here.
//
// Constraints: links are http(s)-only + rel="noopener" (no javascript: URIs),
// title/snippet render as TEXT (Vue escapes - no HTML injection), and the host
// is shown as text with an injected globe icon - NO external favicons (our
// no-external-image-hosts rule).
import { computed } from 'vue';
import Fold from '../Fold.vue';
import { useIcons } from '../../icons';
import type { ToolStep } from '../../types';

const props = defineProps<{ tool: ToolStep; base?: number }>();
const icons = useIcons();

interface WebItem { title: string; url: string; snippet: string; }

function isHttp(url: string): boolean {
  return /^https?:\/\//i.test(url);
}
function hostOf(url: string): string {
  try { return new URL(url).hostname.replace(/^www\./, ''); }
  catch { return url; }
}

const results = computed<WebItem[]>(() => {
  const raw = props.tool.outputFull;
  if (!raw) return [];
  let data: unknown;
  try { data = JSON.parse(raw); } catch { return []; }
  const arr: unknown = Array.isArray(data)
    ? data
    : (data && typeof data === 'object' && Array.isArray((data as { results?: unknown }).results))
      ? (data as { results: unknown[] }).results
      : [];
  if (!Array.isArray(arr)) return [];
  return arr
    .filter((r): r is Record<string, unknown> => !!r && typeof r === 'object')
    .map((r) => ({
      url:     String(r.url ?? ''),
      title:   String(r.title ?? r.url ?? ''),
      snippet: String(r.snippet ?? r.description ?? ''),
    }))
    .filter((r) => isHttp(r.url));
});
</script>

<template>
  <Fold v-if="results.length">
    <template #summary>
      <component :is="icons.globe" :size="13" class="wr__globe" />
      <span>Sources</span>
      <span class="wr__count">{{ results.length }}</span>
    </template>
    <ul class="wr__list" role="list">
      <li v-for="(r, i) in results" :key="i" class="wr__item" :data-cite="(base ?? 0) + i + 1">
        <a :href="r.url" target="_blank" rel="noopener noreferrer" class="wr__title">{{ r.title }}</a>
        <div class="wr__host">{{ hostOf(r.url) }}</div>
        <p v-if="r.snippet" class="wr__snippet">{{ r.snippet }}</p>
      </li>
    </ul>
  </Fold>
</template>

<style scoped>
.wr__globe { flex: 0 0 auto; }
.wr__count { color: var(--ink-subtle); }

.wr__list {
  margin: var(--space-2) 0 0;
  padding: 0;
  list-style: none;
  border: 1px solid var(--border-subtle);
  border-radius: var(--radius-md, 10px);
  background: var(--surface-panel);
  overflow: hidden;
}
.wr__item { padding: var(--space-2) var(--space-3); }
.wr__item + .wr__item { border-top: 1px solid var(--border-subtle); }
.wr__title {
  display: inline-block;
  font-size: var(--text-sm);
  font-weight: 500;
  line-height: 1.35;
  color: var(--ink-display);
  text-decoration: none;
}
.wr__title:hover { text-decoration: underline; }
.wr__host {
  margin-top: 1px;
  font-size: var(--text-xs);
  color: var(--ink-subtle);
}
.wr__snippet {
  margin: var(--space-1) 0 0;
  font-size: var(--text-xs);
  line-height: 1.5;
  color: var(--ink-muted);
  /* Keep cards scannable: clamp long snippets to two lines. */
  display: -webkit-box;
  -webkit-line-clamp: 2;
  line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}
</style>
